import { useQuery } from '@tanstack/react-query'
import type { PropsWithChildren } from 'react'
import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { AdminClient } from '~/api/adminClient'
import type { AdminAuthClient } from '~/auth/authClient'
import { LoadingOverlay } from '~/components/refineUi/layout/loadingOverlay'
import { Button } from '~/components/ui/button'
import { NoTenantPage } from '~/pages/noTenant'
import { NotFoundPage } from '~/pages/notFound'
import { loginRedirectPath } from '~/providers/authProvider'
import { errorMessage, isUnauthorized } from '~/providers/errors'
import { ActiveTenantProvider } from './activeTenant'
import { meInput } from './hostSlug'
import { resolveFallbackTenant } from './resolveFallbackTenant'
import type { TenantRef } from './tenantDecision'
import { decideTenant, hostRole } from './tenantDecision'
import { apexOrigin, resolveTenantHost, tenantOrigin } from './tenantHost'
import { useHostMembershipRecheck } from './useHostMembershipRecheck'

function toRef({ id, slug, name, status }: TenantRef): TenantRef {
  return { id, slug, name, status }
}

async function loadTenantContext(
  client: AdminClient,
  authClient: AdminAuthClient,
  slug: string | null,
) {
  const [me, session] = await Promise.all([
    client.auth.me(meInput()),
    authClient.getSession(),
  ])
  const hostTenant =
    slug === null
      ? null
      : ((
          await client.tenants.list({
            page: 1,
            pageSize: 1,
            filters: [{ field: 'slug', operator: 'eq', value: slug }],
          })
        ).items[0] ?? null)

  const activeId = session.data?.session.activeOrganizationId ?? null
  const fallback = await resolveFallbackTenant({
    candidateIds: [activeId, ...me.tenantIds],
    getTenant: (id) => client.tenants.get({ id }),
    firstTenant: async () =>
      (await client.tenants.list({ page: 1, pageSize: 1 })).items[0] ?? null,
    hasPlatformRole: me.platformRole !== null,
  })

  return {
    hasPlatformRole: me.platformRole !== null,
    hostTenant: hostTenant === null ? null : toRef(hostTenant),
    // the baseline of useHostMembershipRecheck
    hostRole: hostTenant === null ? undefined : hostRole(me, hostTenant.id),
    fallbackTenant: fallback === null ? null : toRef(fallback),
    activeId,
  }
}

/**
 * Resolves the tenant of the current host and renders the app inside it, or
 * redirects / shows not-found / "no tenant" (see decideTenant). Remembers the
 * host's tenant as the session's active tenant for the next apex visit.
 * A tenant user removed from the rendered tenant meanwhile is noticed on
 * their next navigation or window focus (`useHostMembershipRecheck`).
 */
export function TenantGate({
  client,
  authClient,
  baseDomain,
  children,
}: PropsWithChildren<{
  client: AdminClient
  authClient: AdminAuthClient
  baseDomain: string
}>) {
  const { t } = useTranslation()
  const host = resolveTenantHost(window.location.hostname, baseDomain)
  const slug = host.kind === 'tenant' ? host.slug : null
  const { data, isPending, error, refetch, isRefetching } = useQuery({
    queryKey: ['tenantGate', slug],
    queryFn: () => loadTenantContext(client, authClient, slug),
    staleTime: Infinity,
  })

  const decision = useMemo(
    () =>
      data === undefined
        ? null
        : decideTenant({
            host,
            hasPlatformRole: data.hasPlatformRole,
            hostTenant: data.hostTenant,
            fallbackTenant: data.fallbackTenant,
          }),
    // host is derived from slug (baseDomain and location are fixed per load)
    [data, slug],
  )

  const redirectSlug = decision?.type === 'redirect' ? decision.slug : null
  const renderTenantId = decision?.type === 'render' ? decision.tenant.id : null
  const activeId = data?.activeId ?? null
  const activatedRef = useRef<string | null>(null)
  const unauthorized = isUnauthorized(error)
  useHostMembershipRecheck(
    client,
    data?.hasPlatformRole === false ? renderTenantId : null,
    data?.hostRole,
  )

  useEffect(() => {
    // the session ended between <Authenticated>'s check and the gate's load
    if (unauthorized) {
      window.location.replace(loginRedirectPath(window.location))
    }
  }, [unauthorized])

  useEffect(() => {
    if (redirectSlug !== null) {
      window.location.replace(
        tenantOrigin(redirectSlug, baseDomain, window.location),
      )
    }
  }, [redirectSlug, baseDomain])

  useEffect(() => {
    if (
      renderTenantId === null ||
      renderTenantId === activeId ||
      activatedRef.current === renderTenantId
    ) {
      return
    }
    activatedRef.current = renderTenantId
    authClient.organization
      .setActive({ organizationId: renderTenantId })
      .catch((cause: unknown) => {
        console.warn('Could not remember the active tenant', cause)
      })
  }, [renderTenantId, activeId, authClient])

  if (isPending || unauthorized || decision?.type === 'redirect') {
    return (
      <LoadingOverlay loading>
        <div className="min-h-screen" />
      </LoadingOverlay>
    )
  }
  if (error !== null || decision === null) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center">
        <h1 className="text-2xl font-bold">
          {t('shell.tenantGate.loadFailed')}
        </h1>
        <p
          role="alert"
          className="text-muted-foreground text-sm"
        >
          {errorMessage(error) ?? t('shell.tenantGate.fallback')}
        </p>
        <Button
          disabled={isRefetching}
          onClick={() => {
            void refetch()
          }}
        >
          {t('shell.tenantGate.retry')}
        </Button>
      </div>
    )
  }
  if (decision.type === 'notFound') {
    // the host's tenant is unknown: "Back to locations" would loop here
    return <NotFoundPage homeHref={apexOrigin(baseDomain, window.location)} />
  }
  if (decision.type === 'noTenant') {
    return <NoTenantPage />
  }
  return (
    <ActiveTenantProvider tenant={decision.tenant}>
      {children}
    </ActiveTenantProvider>
  )
}
